import {checkLabelValueObject} from '../utils/checkObjects';

/**
 * A class representing all the values in the fields of the header
 */
export class HeaderDetails {
    /**
     * Create a new Header Details
     * @param {LabelValue[]} pslCompanyCodes
     * @param {LabelValue} companyCode
     * @param {LabelValue} purchaseOrganization
     * @param {LabelValue} purchasingGroup
     * @param {LabelValue} businessUnit
     * @param {LabelValue[]} plants
     * @param {LabelValue} defaultPlant
     * @param {LabelValue} defaultStorageLocation
     * @param {DeliveryAddress} defaultDeliveryAddress
     * @param {string} defaultRequestedDeliveryDate
     * @param {string} validityStart
     * @param {string} validityEnd
     * @param {LabelValue} paymentTerms
     * @param {LabelValue} newPaymentTerms
     * @param {LabelValue} orderingAddress
     * @param {LabelValue} invoicePartner
     * @param {{data: LabelValue, manuallyModified: boolean}} incoterms
     * @param {string} supplierStatus
     * @param {boolean} ers
     * @param {boolean} grBasedInvoiceVerification
     * @param {boolean} goodsReceipt
     */
    constructor(
        pslCompanyCodes,
        companyCode,
        purchaseOrganization,
        purchasingGroup,
        businessUnit,
        plants,
        defaultPlant,
        defaultStorageLocation,
        defaultDeliveryAddress,
        defaultRequestedDeliveryDate,
        validityStart,
        validityEnd,
        paymentTerms,
        newPaymentTerms,
        orderingAddress,
        invoicePartner,
        incoterms,
        supplierStatus,
        ers,
        grBasedInvoiceVerification,
        goodsReceipt,
    ) {
        this._pslCompanyCodes = pslCompanyCodes;
        this._companyCode = companyCode;
        this._purchaseOrganization = purchaseOrganization;
        this._purchasingGroup = purchasingGroup;
        this._businessUnit = businessUnit;
        this._plants = plants;
        this._defaultPlant = defaultPlant;
        this._defaultStorageLocation = defaultStorageLocation;
        this._defaultDeliveryAddress = defaultDeliveryAddress;
        this._defaultRequestedDeliveryDate = defaultRequestedDeliveryDate;
        this._validityStart = validityStart;
        this._validityEnd = validityEnd;
        this._paymentTerms = paymentTerms;
        this._newPaymentTerms = newPaymentTerms;
        this._orderingAddress = orderingAddress;
        this._invoicePartner = invoicePartner;
        this._incoterms = incoterms;
        this._supplierStatus = supplierStatus;
        this._ers = ers;
        this._grBasedInvoiceVerification = grBasedInvoiceVerification;
        this._goodsReceipt = goodsReceipt;
    }

    /**
     * Get an JS object containing all the information about the Header Details.
     * @return {{companyCode: LabelValue, incoterms: LabelValue, businessUnit: LabelValue, defaultRequestedDeliveryDate: string, validityEnd: string, purchaseOrganization: LabelValue, defaultDeliveryAddress: DeliveryAddress, invoicePartner: LabelValue, purchasingGroup: LabelValue, validityStart: string, defaultPlant: LabelValue, pslCompanyCodes: LabelValue[], plants: LabelValue[], orderingAddress: LabelValue, defaultStorageLocation: LabelValue, newPaymentTerms: LabelValue, paymentTerms: LabelValue, ebs: boolean, grBasedInvoiceVerification: boolean, goodsReceipt: boolean}}
     */
    getHeaderDetails() {
        return {
            pslCompanyCodes: this._pslCompanyCodes.map((v) => checkLabelValueObject(v)),
            companyCode: checkLabelValueObject(this._companyCode),
            purchaseOrganization: checkLabelValueObject(this._purchaseOrganization),
            purchasingGroup: checkLabelValueObject(this._purchasingGroup),
            businessUnit: this._businessUnit,
            plants: this._plants,
            defaultPlant: this._defaultPlant,
            defaultStorageLocation: this._defaultStorageLocation,
            defaultDeliveryAddress: this._defaultDeliveryAddress,
            defaultRequestedDeliveryDate: this._defaultRequestedDeliveryDate,
            validityStart: this._validityStart,
            validityEnd: this._validityEnd,
            paymentTerms: checkLabelValueObject(this._paymentTerms),
            newPaymentTerms: checkLabelValueObject(this._newPaymentTerms),
            orderingAddress: checkLabelValueObject(this._orderingAddress),
            invoicePartner: checkLabelValueObject(this._invoicePartner),
            incoterms: this._incoterms,
            supplierStatus: this._supplierStatus,
            ers: this._ers,
            grBasedInvoiceVerification: this._grBasedInvoiceVerification,
            goodsReceipt: this._goodsReceipt,
        };
    }

    /**
     * Get the requester value.
     * @return {LabelValue[]} The requester value.
     */
    get pslCompanyCodes() {
        return this._pslCompanyCodes;
    }

    /**
     * Set the pslCompanyCodes value.
     * @param {LabelValue[]} value to set
     */
    set pslCompanyCodes(value) {
        this._pslCompanyCodes = value;
    }

    /**
     * Get the requester value.
     * @return {LabelValue} The requester value.
     */
    get companyCode() {
        return this._companyCode;
    }

    /**
     * Set the companyCode value.
     * @param {LabelValue} value to set
     */
    set companyCode(value) {
        this._companyCode = value;
    }

    /**
     * Get the requester value.
     * @return {LabelValue} The requester value.
     */
    get purchaseOrganization() {
        return this._purchaseOrganization;
    }

    /**
     * Set the purchaseOrganization value.
     * @param {LabelValue} value to set
     */
    set purchaseOrganization(value) {
        this._purchaseOrganization = value;
    }

    /**
     * Get the purchasingGroup value.
     * @return {LabelValue} The requester value.
     */
    get purchasingGroup() {
        return this._purchasingGroup;
    }

    /**
     * Set the purchasingGroup value.
     * @param {LabelValue} value to set
     */
    set purchasingGroup(value) {
        this._purchasingGroup = value;
    }

    /**
     * Get the businessUnit value.
     * @return {LabelValue} The requester value.
     */
    get businessUnit() {
        return this._businessUnit;
    }

    /**
     * Set the businessUnit value.
     * @param {LabelValue} value to set
     */
    set businessUnit(value) {
        this._businessUnit = value;
    }

    /**
     * Get the plants value.
     * @return {LabelValue[]} The plants value.
     */
    get plants() {
        return this._plants;
    }

    /**
     * Set the plants value.
     * @param {LabelValue[]} value to set
     */
    set plants(value) {
        this._plants = value;
    }

    /**
     * Get the plant value.
     * @return {LabelValue} The plant value.
     */
    get defaultPlant() {
        return this._defaultPlant;
    }

    /**
     * Set the plant value.
     * @param {LabelValue} value to set
     */
    set defaultPlant(value) {
        this._defaultPlant = value;
    }

    /**
     * Get the storageLocation value.
     * @return {LabelValue} The storageLocation value.
     */
    get defaultStorageLocation() {
        return this._defaultStorageLocation;
    }

    /**
     * Set the storageLocation value.
     * @param {LabelValue} value to set
     */
    set defaultStorageLocation(value) {
        this._defaultStorageLocation = value;
    }

    /**
     * Get the deliveryAddress value.
     * @return {DeliveryAddress} The deliveryAddress value.
     */
    get defaultDeliveryAddress() {
        return this._defaultDeliveryAddress;
    }

    /**
     * Set the deliveryAddress value.
     * @param {DeliveryAddress} value to set
     */
    set defaultDeliveryAddress(value) {
        this._defaultDeliveryAddress = value;
    }

    /**
     * Get the requestedDeliveryDate value.
     * @return {string} The requestedDeliveryDate value.
     */
    get defaultRequestedDeliveryDate() {
        return this._defaultRequestedDeliveryDate;
    }

    /**
     * Set the requestedDeliveryDate value.
     * @param {string} value to set
     */
    set defaultRequestedDeliveryDate(value) {
        this._defaultRequestedDeliveryDate = value;
    }

    /**
     * Get the validityStart value.
     * @return {string} The validityStart value.
     */
    get validityStart() {
        return this._validityStart;
    }

    /**
     * Set the validityStart value.
     * @param {string} value to set
     */
    set validityStart(value) {
        this._validityStart = value;
    }

    /**
     * Get the validityEnd value.
     * @return {string} The validityEnd value.
     */
    get validityEnd() {
        return this._validityEnd;
    }

    /**
     * Set the validityEnd value.
     * @param {string} value to set
     */
    set validityEnd(value) {
        this._validityEnd = value;
    }

    /**
     * Get the paymentTerms value.
     * @return {LabelValue} The paymentTerms value.
     */
    get paymentTerms() {
        return this._paymentTerms;
    }

    /**
     * Set the paymentTerms value.
     * @param {LabelValue} value to set
     */
    set paymentTerms(value) {
        this._paymentTerms = value;
    }

    /**
     * Get the newPaymentTerms value.
     * @return {LabelValue} The newPaymentTerms value.
     */
    get newPaymentTerms() {
        return this._newPaymentTerms;
    }

    /**
     * Set the newPaymentTerms value.
     * @param {LabelValue} value to set
     */
    set newPaymentTerms(value) {
        this._newPaymentTerms = value;
    }

    /**
     * Get the orderingAddress value.
     * @return {LabelValue} The orderingAddress value.
     */
    get orderingAddress() {
        return this._orderingAddress;
    }

    /**
     * Set the orderingAddress value.
     * @param {LabelValue} value to set
     */
    set orderingAddress(value) {
        this._orderingAddress = value;
    }

    /**
     * Get the invoicePartner value.
     * @return {LabelValue} The invoicePartner value.
     */
    get invoicePartner() {
        return this._invoicePartner;
    }

    /**
     * Set the invoicePartner value.
     * @param {LabelValue} value to set
     */
    set invoicePartner(value) {
        this._invoicePartner = value;
    }

    /**
     * Get the incoterms value.
     * @return {{data: LabelValue, manuallyModified: boolean}} The incoterms value.
     */
    get incoterms() {
        return this._incoterms;
    }

    /**
     * Set the incoterms value.
     * @param {{data: LabelValue, manuallyModified: boolean}} value to set
     */
    set incoterms(value) {
        this._incoterms = value;
    }

    /**
     * Get the supplier status value.
     * @return {string} The supplier status value.
     */
    get supplierStatus() {
        return this._supplierStatus;
    }

    /**
     * Set the supplier status value.
     * @param {string} value to set
     */
    set supplierStatus(value) {
        this._supplierStatus = value;
    }

    /**
     * Get the Evaluated Receipt Settlement value.
     * @return {boolean} The Evaluated Receipt Settlement value.
     */
    get ers() {
        return this._ers;
    }

    /**
     * Set the Evaluated Receipt Settlement value.
     * @param {boolean} value to set
     */
    set ers(value) {
        this._ers = value;
    }

    /**
     * Get the GR-Based Invoice Verification value.
     * @return {boolean} The GR-Based Invoice Verification value.
     */
    get grBasedInvoiceVerification() {
        return this._grBasedInvoiceVerification;
    }

    /**
     * Set the GR-Based Invoice Verification value.
     * @param {boolean} value to set
     */
    set grBasedInvoiceVerification(value) {
        this._grBasedInvoiceVerification = value;
    }

    /**
     * Get the Goods Receipt value.
     * @return {boolean} The Goods Receipt value.
     */
    get goodsReceipt() {
        return this._goodsReceipt;
    }

    /**
     * Set the Goods Receipt value.
     * @param {boolean} value to set
     */
    set goodsReceipt(value) {
        this._goodsReceipt = value;
    }
}
